import {
  ProcessTypeEnum,
  type ApplicationResponse,
  type CreatingApplicationCommand,
} from '@acme/api';
import { BadRequestAlert, useFormModel, useModal } from '@acme/foundation';
import { useUnit } from 'effector-react';
import { Button, Card } from 'flowbite-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  buildActionProps,
  getShouldShowValidation,
  parseBadRequestError,
  useFormHydration,
  type ParsedBadRequest,
} from '../lib';
import { getHeaderTitle } from '../lib/headerTitle';
import type {
  ApplicationFormActionHandlers,
  ApplicationFormSuccessResult,
  CreateApplicationFn,
} from '../model';
import {
  $ctx,
  $formConfig,
  $walletsConfig,
  $walletsCount,
  $walletsCtx,
  CRYPTO_FIELD_IDS,
  addWalletClicked,
  buildCommandFx,
  formMounted,
} from '../model';
import type { ApplicationFormationFormCtx } from '../model/formConfig';
import { ApplicationFormFields } from './fields';

export type { ApplicationFormActionHandlers, ApplicationFormSuccessResult };

interface ApplicationFormProps {
  defaultData?: ApplicationFormationFormCtx;
  application?: ApplicationResponse | null;
  createApplication?: CreateApplicationFn;
  onSuccess?: (result: ApplicationFormSuccessResult) => void;
  actionHandlers?: ApplicationFormActionHandlers;
}

export const ApplicationForm = ({
  defaultData,
  application = null,
  createApplication,
  onSuccess,
  actionHandlers,
}: ApplicationFormProps): React.ReactElement => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [badRequest, setBadRequest] = useState<{
    message: string | null;
    details: Array<{ key?: string | null; value?: string | null }>;
  }>({ message: null, details: [] });
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const {
    ctx,
    formConfig,
    walletsConfig,
    walletsCtx,
    walletsCount,
    onMounted,
    onAddWallet,
    buildCommand,
  } = useUnit({
    ctx: $ctx,
    formConfig: $formConfig,
    walletsConfig: $walletsConfig,
    walletsCtx: $walletsCtx,
    walletsCount: $walletsCount,
    onMounted: formMounted,
    onAddWallet: addWalletClicked,
    buildCommand: buildCommandFx,
  });

  const form = useFormModel({ config: formConfig, ctx });
  const walletsForm = useFormModel({ config: walletsConfig, ctx: walletsCtx });
  const { openConfirmModal } = useModal();

  const prevIsEdit = useRef(!!application);

  useEffect(() => {
    onMounted({ defaultData, isEdit: !!application });
  }, [onMounted, defaultData, application]);

  useEffect(() => {
    const isEdit = !!application;
    if (prevIsEdit.current && !isEdit) {
      form.reset();
      walletsForm.reset();
      setHasAttemptedSubmit(false);
    }
    prevIsEdit.current = isEdit;
  }, [application, form, walletsForm]);

  useEffect(() => {
    setBadRequest({ message: null, details: [] });
  }, [application?.id]);

  useFormHydration(form, formConfig, defaultData, application?.id ?? undefined);

  const shouldShowFormValidation = getShouldShowValidation(
    form.state.submitCount,
    Object.keys(form.state.touched).length,
    hasAttemptedSubmit
  );
  const shouldShowWalletsValidation = getShouldShowValidation(
    walletsForm.state.submitCount,
    Object.keys(walletsForm.state.touched).length,
    hasAttemptedSubmit
  );

  useEffect(() => {
    if (!shouldShowFormValidation) return;
    form.runValidate();
  }, [
    shouldShowFormValidation,
    form.state.values,
    form.state.touched,
    form.runValidate,
  ]);

  useEffect(() => {
    if (!shouldShowWalletsValidation) return;
    walletsForm.runValidate();
  }, [
    shouldShowWalletsValidation,
    walletsForm.state.values,
    walletsForm.state.touched,
    walletsForm.runValidate,
  ]);

  const hasFormErrors = Object.values(form.state.errors).some(Boolean);
  const hasWalletsErrors = Object.values(walletsForm.state.errors).some(
    Boolean
  );
  const isFormInvalid =
    shouldShowFormValidation && (hasFormErrors || hasWalletsErrors);

  const currentType = Number(
    form.state.values.applicationType ?? ctx.applicationType
  );
  const showIncomingWallets = currentType === ProcessTypeEnum.Incoming;

  useEffect(() => {
    if (currentType === ProcessTypeEnum.Incoming) {
      form.onChange('isCryptoPayment', true);
    }
  }, [currentType, form.onChange]);

  const handleSubmit = useCallback(
    async (asDraft: boolean) => {
      if (!createApplication || !onSuccess) return;
      const ok = await openConfirmModal({
        title: asDraft ? 'Сохранить как черновик?' : 'Отправить заявку?',
        content: asDraft
          ? 'Вы уверены, что хотите сохранить заявку как черновик? После этого вы сможете отправить её на проверку.'
          : 'Вы уверены, что хотите отправить заявку на проверку?',
      });
      if (!ok) return;
      setBadRequest({ message: null, details: [] });
      setHasAttemptedSubmit(true);
      const formErrors = form.runValidate();
      const walletsErrors = walletsForm.runValidate();
      if (
        Object.values(formErrors).some(Boolean) ||
        Object.values(walletsErrors).some(Boolean)
      ) {
        return;
      }
      setIsSubmitting(true);
      try {
        const command = await buildCommand({
          mode: asDraft ? 'draft' : 'review',
          form,
          walletsForm,
          walletsCount,
          showIncomingWallets,
        });
        const result = await createApplication(
          command as CreatingApplicationCommand
        );
        const code = result.applicationCode ?? '';
        onSuccess({
          type: 'confirmed',
          title: asDraft
            ? `Заявка ${code} сохранена как черновик!`.trim()
            : `Заявка ${code} отправлена на проверку!`.trim(),
          message: asDraft
            ? 'Отправьте заявку на проверку для её модерации'
            : 'Ожидайте результата модерации',
          applicationId: result.applicationId,
          applicationCode: result.applicationCode ?? null,
        });
      } catch (err) {
        const parsed = parseBadRequestError(err);
        if (parsed) {
          setBadRequest({ message: parsed.message, details: parsed.details });
        } else {
          throw err;
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      createApplication,
      onSuccess,
      buildCommand,
      form,
      walletsForm,
      walletsCount,
      showIncomingWallets,
      openConfirmModal,
    ]
  );

  const onBadRequest = useCallback((parsed: ParsedBadRequest) => {
    setBadRequest({ message: parsed.message, details: parsed.details });
  }, []);

  const saveDraftHandler = useMemo(
    () =>
      buildActionProps({
        application,
        actionHandlers: actionHandlers ?? null,
        buildCommand,
        form,
        walletsForm,
        walletsCount,
        showIncomingWallets,
        onBadRequest,
      })?.props.rework?.editDraft?.handler,
    [
      application,
      actionHandlers,
      buildCommand,
      form,
      walletsForm,
      walletsCount,
      showIncomingWallets,
      onBadRequest,
    ]
  );

  const handleSaveDraft = useCallback(async () => {
    if (!saveDraftHandler) return;
    const ok = await openConfirmModal({
      title: 'Сохранить черновик?',
      content: 'Вы уверены, что хотите сохранить изменения в черновике заявки?',
    });
    if (!ok) return;
    setBadRequest({ message: null, details: [] });
    setHasAttemptedSubmit(true);
    const formErrors = form.runValidate();
    const walletsErrors = walletsForm.runValidate();
    if (
      Object.values(formErrors).some(Boolean) ||
      Object.values(walletsErrors).some(Boolean)
    ) {
      return;
    }
    setIsSubmitting(true);
    try {
      await saveDraftHandler();
    } finally {
      setIsSubmitting(false);
    }
  }, [saveDraftHandler, form, walletsForm, openConfirmModal]);

  const headerTitle = useMemo(
    () => getHeaderTitle(form.state.values.name, ctx.name),
    [form.state.values.name, ctx.name]
  );

  return (
    <Card>
      <h2 className="text-xl font-semibold">{headerTitle}</h2>
      <ApplicationFormFields
        form={form}
        walletsForm={walletsForm}
        cryptoFieldIds={[...CRYPTO_FIELD_IDS]}
        showIncomingWallets={showIncomingWallets}
        onAddWallet={onAddWallet}
      />
      <BadRequestAlert
        message={badRequest.message}
        details={badRequest.details}
      />

      {(application?.id && saveDraftHandler) || !application ? (
        <div className="flex justify-end gap-2">
          {application && saveDraftHandler && (
            <Button
              color="alternative"
              disabled={isSubmitting || isFormInvalid}
              onClick={handleSaveDraft}
            >
              {isSubmitting ? 'Сохранение...' : 'Сохранить черновик'}
            </Button>
          )}
          {!application && (
            <>
              <Button
                color="alternative"
                disabled={isSubmitting || isFormInvalid}
                onClick={() => handleSubmit(true)}
              >
                {isSubmitting ? 'Отправка...' : 'Сохранить как черновик'}
              </Button>
              <Button
                color="primary"
                disabled={isSubmitting || isFormInvalid}
                onClick={() => handleSubmit(false)}
              >
                {isSubmitting ? 'Отправка...' : 'Отправить'}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </Card>
  );
};
